import React, { useState } from 'react';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { GeneralInsights } from '../types';
import { useUser } from '../contexts/UserContext';
import GeneralInsightsEditor from './GeneralInsightsEditor';
import EditIcon from '@mui/icons-material/Edit';

interface GeneralInsightsSectionProps {
  generalInsights: GeneralInsights;
  clientId: string;
  onUpdate: (updatedInsights: GeneralInsights) => void;
}

type TabType = 'keyLearnings' | 'opportunities' | 'challenges';

const GeneralInsightsSection: React.FC<GeneralInsightsSectionProps> = ({ 
  generalInsights, 
  clientId, 
  onUpdate 
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('keyLearnings');
  const [isEditing, setIsEditing] = useState(false);
  const { user } = useUser();

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleEditorClose = () => {
    setIsEditing(false);
  };

  const handleEditorSave = (updatedInsights: GeneralInsights) => {
    onUpdate(updatedInsights);
    setIsEditing(false);
  };

  const getTabContent = () => {
    // Add comprehensive checks to prevent runtime errors
    if (!generalInsights || typeof generalInsights !== 'object') {
      return {
        title: 'Loading...',
        items: []
      };
    }

    switch (activeTab) {
      case 'keyLearnings':
        return {
          title: generalInsights.keyLearnings?.title,
          items: generalInsights.keyLearnings?.items || []
        };
      case 'opportunities':
        return {
          title: generalInsights.opportunities?.title,
          items: generalInsights.opportunities?.items || []
        };
      case 'challenges':
        return {
          title: generalInsights.challenges?.title,
          items: generalInsights.challenges?.items || []
        };
      default:
        return {
          title: generalInsights.keyLearnings?.title,
          items: generalInsights.keyLearnings?.items || []
        };
    }
  };

  const getTabStyle = (tabType: TabType) => ({
    flex: 1,
    padding: '18px 28px 14px 28px',
    borderBottom: activeTab === tabType ? '2px solid #111' : '2px solid transparent',
    color: activeTab === tabType ? '#111' : '#888',
    background: activeTab === tabType ? '#f9fafb' : 'transparent',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 18,
    transition: 'all 0.2s ease',
    border: 'none',
    borderRadius: 0,
  });

  const content = getTabContent();

  return (
    <>
      <section id="general-insights" className="mb-16">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-satoshi font-bold text-3xl m-0 flex items-center gap-3">
            <LightbulbOutlinedIcon className="text-3xl" />
            General Insights
          </h2>
          {user?.canEdit ? <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick();
              }}
              style={{
                background: 'transparent',
                color: '#3b82f6',
                border: '1px solid #bfdbfe',
                borderRadius: 8,
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#eff6ff';
                e.currentTarget.style.borderColor = '#3b82f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = '#bfdbfe';
              }}
            >
              <EditIcon style={{ fontSize: 14 }} />
              Edit
            </button>
          </div> : null}
        </div>

        <div className="space-y-6">
          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            <button 
              style={getTabStyle('keyLearnings')}
              onClick={() => setActiveTab('keyLearnings')}
            >
              Key Learnings
            </button>
            <button 
              style={getTabStyle('opportunities')}
              onClick={() => setActiveTab('opportunities')}
            >
              Opportunities
            </button>
            <button 
              style={getTabStyle('challenges')}
              onClick={() => setActiveTab('challenges')}
            >
              Challenges
            </button>
          </div>

          {/* Content */}
          <div>
            <h3 className="font-satoshi font-bold text-lg mb-4">{content.title}</h3>
            {content.items.length > 0 ? (
              <ul className="space-y-3 list-none p-0 m-0">
                {content.items.map((item, index) => (
                  <li key={index} className="font-satoshi text-lg flex items-start gap-2">
                    <span className="text-black">•</span>
                    <span className="flex-1">
                      {item.text} <span className="ml-1">{item.emoji}</span>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-gray-500 text-lg italic">
                No {activeTab === 'keyLearnings' ? 'key learnings' : activeTab} available yet.
              </div>
            )}
          </div>
        </div>
      </section>
      
      <GeneralInsightsEditor
        isOpen={isEditing}
        onClose={handleEditorClose}
        clientId={clientId}
        initialInsights={generalInsights}
        onSave={handleEditorSave}
      />
    </>
  );
};

export default GeneralInsightsSection; 